import { useState, useRef, useCallback } from 'react';
import { API_BASE_URL, getAuthHeaders } from '../auth';

const SAMPLE_RATE = 16000;

/** Pick the best MediaRecorder container the browser supports (opus is compact). */
function pickAudioMimeType() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4', // Safari
  ];
  return candidates.find(t => MediaRecorder.isTypeSupported(t)) || '';
}

export default function useMeetingSession() {
  const [sessionState, setSessionState] = useState('idle');
  const [sessionId, setSessionId] = useState(null);
  const [transcriptSegments, setTranscriptSegments] = useState([]);
  const [partialText, setPartialText] = useState('');
  const [minutes, setMinutes] = useState(null);
  const [error, setError] = useState(null);
  const [transcriptVersion, setTranscriptVersion] = useState(0);
  const [rediarizationDiff] = useState(null); // kept for API compatibility; always null in upload mode
  const [sessionTitle, setSessionTitle] = useState(null);

  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const processorRef = useRef(null);
  const mediaRecorderRef = useRef(null);   // records the whole meeting locally
  const recordedChunksRef = useRef([]);    // Blob parts collected during recording
  const sessionIdRef = useRef(null);       // stable id for the upload on stop
  const stoppedRef = useRef(false);

  const cleanup = useCallback(() => {
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
    }
    if (mediaRecorderRef.current) {
      try {
        if (mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
      } catch (e) { /* ignore */ }
      mediaRecorderRef.current = null;
    }
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }
    wsRef.current = null;
  }, []);

  const startSession = useCallback(async (language = 'de') => {
    setError(null);
    setTranscriptSegments([]);
    setPartialText('');
    setMinutes(null);
    setSessionTitle(null);
    stoppedRef.current = false;

    setSessionState('connecting');

    try {
      // 1. Create session via REST
      const res = await fetch(`${API_BASE_URL}/api/meeting/start`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ language }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to start session');
      }

      const session = await res.json();
      const sid = session.id;
      setSessionId(sid);

      // 2. Get microphone
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { sampleRate: SAMPLE_RATE, channelCount: 1, echoCancellation: true, noiseSuppression: true }
      });
      mediaStreamRef.current = stream;

      // 3. Record the whole meeting locally. There is no live WebSocket — on
      //    stop we upload the recording and transcribe it server-side via the
      //    pre-recorded API. This works reliably through any reverse proxy.
      sessionIdRef.current = sid;
      stoppedRef.current = false;
      const mimeType = pickAudioMimeType();
      // Cap the bitrate: 32 kbps opus is clear for speech and keeps the upload
      // small (~1.2 MB per 5 min) so it stays under typical proxy body limits.
      const recorderOpts = { audioBitsPerSecond: 32000 };
      if (mimeType) recorderOpts.mimeType = mimeType;
      const recorder = new MediaRecorder(stream, recorderOpts);
      mediaRecorderRef.current = recorder;
      recordedChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      // Flush a chunk every second so an unexpected stop loses at most ~1s.
      recorder.start(1000);
      setSessionState('recording');

    } catch (err) {
      setError(err.message);
      setSessionState('error');
      cleanup();
    }
  }, [cleanup]);

  const stopSession = useCallback(async (uiLanguage, speakerNames) => {
    stoppedRef.current = true;
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      cleanup();
      return;
    }

    setSessionState('generating_minutes');

    // Stop recording and wait for the final chunk to flush into a single Blob.
    const mimeType = recorder.mimeType || 'audio/webm';
    const audioBlob = await new Promise((resolve) => {
      recorder.onstop = () => resolve(new Blob(recordedChunksRef.current, { type: mimeType }));
      try { recorder.stop(); } catch (e) {
        resolve(new Blob(recordedChunksRef.current, { type: mimeType }));
      }
    });

    // Release the microphone now that recording is done.
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
    }

    const sid = sessionIdRef.current;
    try {
      const form = new FormData();
      const ext = mimeType.includes('mp4') ? 'mp4' : (mimeType.includes('ogg') ? 'ogg' : 'webm');
      form.append('audio', audioBlob, `meeting.${ext}`);
      form.append('language', uiLanguage || 'de');
      if (speakerNames && Object.keys(speakerNames).length > 0) {
        form.append('speaker_names', JSON.stringify(speakerNames));
      }
      // Do NOT set Content-Type — the browser adds the multipart boundary itself.
      const res = await fetch(`${API_BASE_URL}/api/meeting/${sid}/upload`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: form,
      });
      if (!res.ok) {
        let msg = `Upload failed (${res.status})`;
        try { const e = await res.json(); if (e.error) msg = e.error; } catch (_) { /* ignore */ }
        throw new Error(msg);
      }
      const data = await res.json();
      if (Array.isArray(data.transcript)) {
        setTranscriptSegments(data.transcript.map(s => ({
          text: s.text,
          speaker_id: s.speaker_id,
          start_time: s.start_time,
          end_time: s.end_time,
        })));
        setTranscriptVersion(v => v + 1);
      }
      if (data.minutes) {
        setMinutes({
          summary: data.minutes.summary,
          topics: data.minutes.topics,
          decisions: data.minutes.decisions,
          action_items: data.minutes.action_items,
        });
      }
      if (data.title) setSessionTitle(data.title);
      setSessionState('completed');
    } catch (err) {
      setError(err.message || 'Upload failed');
      setSessionState('error');
    } finally {
      cleanup();
    }
  }, [cleanup]);

  return {
    startSession,
    stopSession,
    sessionState,
    sessionId,
    transcriptSegments,
    transcriptVersion,
    rediarizationDiff,
    partialText,
    minutes,
    error,
    sessionTitle,
    setSessionTitle,
  };
}
